import { toSortedCompat } from '@sweetistics/contracts/utils/sort';
import { type QueryClient, skipToken, useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import type { z } from 'zod';
import { getBrowserTrpcClient } from '@/lib/trpc/browser-client';
import { trpc } from '@/lib/trpc/client';
import { ConversationResponseFromApiSchema } from '@/lib/validation/api/conversation';
import type { Tweet as TweetType } from '@/types/tweet';

type ConversationResponse = z.infer<typeof ConversationResponseFromApiSchema>;

export interface ConversationThreadMeta {
  depth: number;
  hasReplies: boolean;
  isOriginalPoster: boolean;
}

export interface ConversationThreadTweet extends TweetType {
  _conversationMeta: ConversationThreadMeta;
}

export interface ConversationThreadResult {
  conversation: ConversationResponse['conversation'];
  profiles: ConversationResponse['profiles'];
  tweetIds: string[];
  tweets: Record<string, ConversationThreadTweet>;
}

export interface UseConversationThreadOptions {
  enabled?: boolean;
  gcTime?: number;
  staleTime?: number;
}

const DEFAULT_STALE_TIME = 60 * 60 * 1000;
const DEFAULT_GC_TIME = 24 * 60 * 60 * 1000;

const parseConversationResponse = (payload: unknown): ConversationResponse =>
  ConversationResponseFromApiSchema.parse(payload);

export const conversationQueryKey = (tweetId: string) => getQueryKey(trpc.tweets.getConversation, { tweetId });

function buildConversationThread(data: ConversationResponse | null): ConversationThreadResult | null {
  if (data == null) {
    return null;
  }

  const { tweets, profiles, tweetIds, conversation } = data;
  const getTweet = (id: string | null | undefined): TweetType | undefined => (id == null ? undefined : tweets[id]);
  const rootAuthorId = getTweet(conversation.rootTweetId)?.authorId ?? null;

  const denormalizedTweets: Record<string, ConversationThreadTweet> = {};
  const setDenormalizedTweet = (id: string, value: ConversationThreadTweet) => {
    Object.defineProperty(denormalizedTweets, id, {
      value,
      writable: true,
      enumerable: true,
      configurable: true,
    });
  };
  const childrenMap = new Map<string, string[]>();
  const depthMap = new Map<string, number>();

  const parentIdById = new Map<string, string | null>();
  for (const id of tweetIds) {
    const candidate = getTweet(id);
    const parentReference = candidate?.referencedTweets?.find(
      (reference) => reference?.type === 'repliedTo' && typeof reference?.id === 'string'
    );
    parentIdById.set(id, parentReference?.id ?? null);
  }

  for (const [id, parentId] of parentIdById.entries()) {
    if (parentId != null && getTweet(parentId)) {
      const existingChildren = childrenMap.get(parentId);
      if (existingChildren) {
        existingChildren.push(id);
      } else {
        childrenMap.set(parentId, [id]);
      }
    }
  }

  const computeDepth = (id: string, visited = new Set<string>()): number => {
    if (visited.has(id)) {
      return 0;
    }
    visited.add(id);

    const cached = depthMap.get(id);
    if (cached !== undefined) {
      return cached;
    }

    const parentId = parentIdById.get(id) ?? null;
    let depth = 0;
    if (parentId !== null && getTweet(parentId)) {
      depth = computeDepth(parentId, visited) + 1;
    }

    depthMap.set(id, depth);
    return depth;
  };

  for (const id of tweetIds) {
    computeDepth(id);
  }

  for (const id of tweetIds) {
    const tweet = getTweet(id);
    if (!tweet) {
      continue;
    }
    const profile = tweet.authorId ? profiles[tweet.authorId] : undefined;
    const depth = depthMap.get(id) ?? 0;
    const isOriginalPoster = rootAuthorId !== null && tweet.authorId === rootAuthorId;
    const normalizedAuthor =
      tweet.author && profile
        ? {
            ...tweet.author,
            profileImageUrl: tweet.author.profileImageUrl ?? profile.profileImageUrl ?? null,
            name: tweet.author.name ?? profile.name ?? tweet.author.name,
            username: tweet.author.username ?? profile.username ?? tweet.author.username,
          }
        : tweet.author;

    const conversationTweet: ConversationThreadTweet = {
      ...tweet,
      ...(normalizedAuthor ? { author: normalizedAuthor } : {}),
      referencedTweets: Array.isArray(tweet.referencedTweets) ? tweet.referencedTweets : [],
      _conversationMeta: {
        isOriginalPoster,
        depth,
        hasReplies: childrenMap.has(id),
      },
    };

    setDenormalizedTweet(id, conversationTweet);
  }

  const sortedTweetIds = toSortedCompat(tweetIds, (a, b) => {
    const depthA = depthMap.get(a) ?? 0;
    const depthB = depthMap.get(b) ?? 0;
    if (depthA !== depthB) {
      return depthA - depthB;
    }

    const timeA = new Date(getTweet(a)?.createdAt ?? 0).getTime();
    const timeB = new Date(getTweet(b)?.createdAt ?? 0).getTime();
    return timeA - timeB;
  });

  return {
    tweets: denormalizedTweets,
    profiles,
    tweetIds: sortedTweetIds,
    conversation,
  };
}

export function useConversationThread(
  tweetId: string | null,
  { enabled = true, staleTime = DEFAULT_STALE_TIME, gcTime = DEFAULT_GC_TIME }: UseConversationThreadOptions = {}
) {
  const incompleteRefetchReference = useRef(false);
  const lastTweetIdReference = useRef<string | null>(null);

  if (lastTweetIdReference.current !== tweetId) {
    lastTweetIdReference.current = tweetId;
    incompleteRefetchReference.current = false;
  }
  const queryClient = useQueryClient();
  const trpcClient = getBrowserTrpcClient();
  const query = trpc.tweets.getConversation.useQuery(tweetId ? { tweetId } : skipToken, {
    enabled: Boolean(tweetId) && enabled,
    staleTime,
    gcTime,
    select: parseConversationResponse,
  });

  const thread = useMemo(() => buildConversationThread(query.data ?? null), [query.data]);

  const refetch = useCallback(
    async (options?: { force?: boolean; throwOnError?: boolean }) => {
      const runQueryRefetch = (flag?: boolean) => {
        if (flag === undefined) {
          return query.refetch();
        }
        return query.refetch({ throwOnError: flag });
      };
      if (tweetId === undefined || tweetId === null) {
        return runQueryRefetch(options?.throwOnError);
      }
      if (options?.force) {
        try {
          const response = await trpcClient.tweets.getConversation.query({ tweetId, force: 'true' });
          const parsed = parseConversationResponse(response);
          queryClient.setQueryData(conversationQueryKey(tweetId), parsed);
        } catch (error) {
          if (options?.throwOnError) {
            throw error;
          }
        }
        return runQueryRefetch(options?.throwOnError);
      }
      return runQueryRefetch(options?.throwOnError);
    },
    [query, queryClient, trpcClient, tweetId]
  );

  const { isFetching } = query;

  useLayoutEffect(() => {
    if (!thread || isFetching) {
      return;
    }
    const needsForce = thread.conversation && (!thread.conversation.isComplete || thread.conversation.missingTweets);
    if (needsForce && !incompleteRefetchReference.current) {
      incompleteRefetchReference.current = true;
      refetch({ throwOnError: false, force: true }).catch((error: unknown) => {
        console.error('Conversation refetch failed', error);
      });
    }
  }, [thread, isFetching, refetch]);

  return { ...query, thread, refetch };
}

export type UseConversationThreadReturn = ReturnType<typeof useConversationThread>;

interface PrefetchConversationThreadOptions extends UseConversationThreadOptions {
  fetchConversation: (input: { tweetId: string }) => Promise<unknown>;
  queryClient: QueryClient;
  tweetId: string;
}

export async function prefetchConversationThread({
  fetchConversation,
  queryClient,
  tweetId,
  staleTime = DEFAULT_STALE_TIME,
  gcTime = DEFAULT_GC_TIME,
}: PrefetchConversationThreadOptions): Promise<void> {
  if (tweetId === undefined) {
    return;
  }

  await queryClient.prefetchQuery({
    queryKey: conversationQueryKey(tweetId),
    queryFn: async () => {
      const response = await fetchConversation({ tweetId });
      return parseConversationResponse(response);
    },
    staleTime,
    gcTime,
  });
}

export function usePrefetchConversationThread(queryClient: QueryClient, options: UseConversationThreadOptions = {}) {
  const trpcClient = getBrowserTrpcClient();
  const { staleTime = DEFAULT_STALE_TIME, gcTime = DEFAULT_GC_TIME } = options;

  return useCallback(
    async (tweetId: string) => {
      // The hook always reuses the provider-scoped tRPC client so every caller
      // shares batching/inflight state instead of instantiating ad-hoc clients.
      await prefetchConversationThread({
        fetchConversation: async (input) => trpcClient.tweets.getConversation.query(input),
        queryClient,
        tweetId,
        staleTime,
        gcTime,
      });
    },
    [gcTime, queryClient, staleTime, trpcClient]
  );
}

export { buildConversationThread };
